import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { getAuth } from 'firebase/auth';
import { isUserBlocked } from '../../utils/localstorage';
import { API_URL } from '../../constants/string-constants';

const MenuContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 4px; /* Align with top of message box */
  gap: 4px; /* Space between ... menu and heart button */
`;

const MenuButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: var(--clr-sec);
  padding: 4px 8px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  opacity: 0;
  transition: opacity 0.2s ease, background-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  ${(props) =>
    props.$visible &&
    `
    opacity: 1;
  `}
`;

const DropdownMenu = styled.div`
  position: fixed;
  background: var(--clr-prim);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 10000;
  min-width: 180px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  animation: dropdownAppear 0.2s ease-out;

  @keyframes dropdownAppear {
    0% {
      opacity: 0;
      transform: translateX(10px);
    }
    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @media (max-width: 768px) {
    min-width: 160px;
  }
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 16px 16px;
  background: none;
  border: none;
  color: var(--clr-sec);
  text-align: left;
  cursor: pointer;
  font-size: 15px;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

const BanMenuItem = styled(MenuItem)`
  color: #ff4444;

  &:hover {
    background: rgba(255, 68, 68, 0.1);
  }
`;

const WarnMenuItem = styled(MenuItem)`
  color: #ff9500;

  &:hover {
    background: rgba(255, 149, 0, 0.1);
  }
`;

const EmojiButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: var(--clr-sec);
  padding: 4px 8px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 16px;
  opacity: 0;
  transition: opacity 0.2s ease, background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  ${props => props.$visible && `
    opacity: 1;
  `}
`;

const EmojiDropdown = styled.div`
  position: fixed;
  background: var(--clr-prim);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 10000;
  padding: 8px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
  min-width: 180px;
  animation: emojiDropdownAppear 0.2s ease-out;
  
  @keyframes emojiDropdownAppear {
    0% {
      opacity: 0;
      transform: scale(0.9);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const EmojiOption = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.1s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const SearchButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--clr-sec);
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  transition: background-color 0.2s ease;
  grid-column: span 4;
  margin-top: 4px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FullEmojiPickerContainer = styled.div`
  .em-emoji-picker {
    --rgb-background: var(--clr-prim);
    --rgb-color: var(--clr-sec);
    --rgb-accent: var(--clr-purple);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    overflow: hidden;
  }
`;

const MessageHighlightOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  z-index: 100;
  pointer-events: none;
`;

const HighlightedMessageOutline = styled.div`
  position: fixed;
  background: transparent;
  border: 2px solid var(--clr-purple);
  border-radius: 12px;
  z-index: 100;
  pointer-events: none;
  box-shadow: 0 0 20px rgba(147, 51, 234, 0.3);
  animation: highlightPulse 0.3s ease-out;

  @keyframes highlightPulse {
    0% {
      opacity: 0;
      transform: scale(0.95);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const ReactionsModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ReactionsModal = styled.div`
  background: var(--clr-bg);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  min-width: 280px;
  max-width: 360px;
  max-height: 80vh;
  overflow: hidden;
  animation: slideUp 0.2s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ReactionsModalHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ReactionsModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  color: var(--clr-sec);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--clr-sec);
  font-size: 20px;
  cursor: pointer;
  padding: 4px 8px;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const ReactionTabs = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ReactionTab = styled.button`
  background: none;
  border: none;
  padding: 12px 16px;
  color: var(--clr-sec);
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  border-bottom: 2px solid ${props => props.$active ? 'var(--clr-purple)' : 'transparent'};
  opacity: ${props => props.$active ? 1 : 0.7};
  transition: opacity 0.2s, border-color 0.2s;
  white-space: nowrap;

  &:hover {
    opacity: 1;
  }
`;

const ReactionCount = styled.span`
  font-size: 12px;
  opacity: 0.7;
`;

const UsersList = styled.div`
  padding: 8px 0;
  max-height: 300px;
  overflow-y: auto;
`;

const UserItem = styled.div`
  padding: 10px 16px;
  color: var(--clr-sec);
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const LoadingText = styled.div`
  padding: 20px;
  text-align: center;
  color: var(--clr-sec);
  opacity: 0.7;
`;

const MessageActionsMenu = ({
  message,
  isMod = false,
  onFriendRequest,
  onReport,
  onReply,
  onBlock,
  onBan,
  onWarn,
  onViewProfile,
  onAddReaction,
  onRemoveReaction,
  currentUserId,
  hasSubscription = false,
  isHovered = false,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [showFullEmojiPicker, setShowFullEmojiPicker] = useState(false);
  const [emojiPickerLoading, setEmojiPickerLoading] = useState(false);
  const [EmojiPickerComponent, setEmojiPickerComponent] = useState(null);
  const [emojiData, setEmojiData] = useState(null);
  const [messageRect, setMessageRect] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [emojiDropdownPosition, setEmojiDropdownPosition] = useState(null);
  const [showReactionsModal, setShowReactionsModal] = useState(false);
  const [reactionsData, setReactionsData] = useState({});
  const [activeReactionTab, setActiveReactionTab] = useState(null);
  const [loadingReactions, setLoadingReactions] = useState(false);
  const menuRef = useRef();
  const emojiRef = useRef();
  const emojiButtonRef = useRef();
  const emojiPickerRef = useRef();

  // Available emoji reactions - just use the emoji directly as the type
  const emojiOptions = ['❤️', '😂', '👍', '😮', '💔', '😭', '☝️', '🔥', '👀', '😢', '❓', '💀'];

  // Helper to extract emoji from reaction type
  const getEmojiFromType = (reactionType) => {
    if (reactionType.startsWith('unicode:')) {
      return reactionType.slice(8);
    }
    if (reactionType === 'heart') {
      return '❤️';
    }
    return reactionType;
  };

  // Get reactions that have counts > 0
  const activeReactions = Object.entries(message.reactions || {})
    .filter(([, count]) => count > 0);
  const hasReactions = activeReactions.length > 0;

  // Fetch users for a specific reaction type
  const fetchReactionUsers = async (reactionType) => {
    if (reactionsData[reactionType]) return; // Already fetched

    setLoadingReactions(true);
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        console.error('Not authenticated');
        return;
      }

      const token = await auth.currentUser.getIdToken();
      const encodedType = encodeURIComponent(reactionType);
      const url = `${API_URL}messages/${message.id}/reactions/${encodedType}/users`;
      const response = await fetch(url, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setReactionsData(prev => ({
          ...prev,
          [reactionType]: data.users
        }));
      } else {
        console.error('Failed to fetch reaction users, status:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch reaction users:', error);
    } finally {
      setLoadingReactions(false);
    }
  };

  // Handle opening the reactions modal
  const handleViewReactions = () => {
    setIsDropdownOpen(false);
    setMessageRect(null);
    setDropdownPosition(null);
    setShowReactionsModal(true);

    // Set first reaction as active tab and fetch its users
    if (activeReactions.length > 0) {
      const firstReaction = activeReactions[0][0];
      setActiveReactionTab(firstReaction);
      fetchReactionUsers(firstReaction);
    }
  };

  // Handle tab change
  const handleTabChange = (reactionType) => {
    setActiveReactionTab(reactionType);
    fetchReactionUsers(reactionType);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside the fixed dropdown (which is in a portal)
      const dropdownElement = document.querySelector('[data-dropdown-menu]');
      const emojiDropdownElement = document.querySelector('[data-emoji-dropdown]');
      const fullEmojiPickerElement = document.querySelector('[data-full-emoji-picker]');
      
      if (isDropdownOpen && dropdownElement && !dropdownElement.contains(event.target) && 
          menuRef.current && !menuRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setMessageRect(null);
        setDropdownPosition(null);
      }
      
      if (isEmojiPickerOpen && emojiDropdownElement && !emojiDropdownElement.contains(event.target) &&
          emojiRef.current && !emojiRef.current.contains(event.target)) {
        setIsEmojiPickerOpen(false);
        setMessageRect(null);
        setEmojiDropdownPosition(null);
        setShowFullEmojiPicker(false);
      }
      
      // Skip click outside detection for full emoji picker - let it handle its own closing
      // The full emoji picker will close itself when an emoji is selected via handleEmojiSelect
    };

    // Only add click outside listener when NOT showing the full emoji picker
    if ((isDropdownOpen || isEmojiPickerOpen) && !showFullEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen, isEmojiPickerOpen, showFullEmojiPicker]);


  // Update ONLY message highlight position on scroll (dropdowns stay fixed)
  useEffect(() => {
    const updateMessageRect = () => {
      if ((isDropdownOpen || isEmojiPickerOpen) && menuRef.current) {
        const messageElement = menuRef.current.closest('[data-message-id]');
        if (messageElement) {
          const chatMessage = messageElement.querySelector('[data-chat-message]');
          if (chatMessage) {
            const rect = chatMessage.getBoundingClientRect();
            setMessageRect({
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height
            });
          }
        }
      }
    };

    if (isDropdownOpen || isEmojiPickerOpen) {
      // Update on scroll
      window.addEventListener('scroll', updateMessageRect, true); // Use capture to catch all scroll events
      // Also update on resize in case the window size changes
      window.addEventListener('resize', updateMessageRect);
      
      return () => {
        window.removeEventListener('scroll', updateMessageRect, true);
        window.removeEventListener('resize', updateMessageRect);
      };
    }
  }, [isDropdownOpen, isEmojiPickerOpen]);


  const handleMenuClick = (e) => {
    e.stopPropagation();
    
    if (!isDropdownOpen) {
      // Opening dropdown - capture message position for highlighting and dropdown positioning
      const messageElement = e.target.closest('[data-message-id]');
      if (messageElement) {
        const chatMessage = messageElement.querySelector('[data-chat-message]');
        if (chatMessage) {
          const rect = chatMessage.getBoundingClientRect();
          setMessageRect({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height
          });
          
          // Calculate dropdown position (to the right of the message)
          const dropdownWidth = 200; // Approximate width
          const dropdownHeight = 320; // Approximate height (larger buttons now)
          const spacing = 16; // Space between message and dropdown
          const screenPadding = 10; // Minimum padding from screen edge
          const bottomSafeZone = 80; // Extra padding to avoid input bar at bottom

          let left = rect.right + spacing;
          let top = rect.top + (rect.height / 2) - (dropdownHeight / 2);

          // Check if dropdown would go off screen right
          const screenWidth = window.innerWidth;
          if (left + dropdownWidth > screenWidth - screenPadding) {
            // Try to position as far right as possible without going off screen
            left = screenWidth - dropdownWidth - screenPadding;

            // If that would overlap the message, try positioning to the left
            if (left < rect.right && rect.left - dropdownWidth - spacing > screenPadding) {
              left = rect.left - dropdownWidth - spacing;
            }
          }

          // Check if dropdown would go off screen top/bottom
          const screenHeight = window.innerHeight;
          if (top < screenPadding) {
            top = screenPadding;
          } else if (top + dropdownHeight > screenHeight - bottomSafeZone) {
            // Position above the click point if near bottom, accounting for input bar
            top = screenHeight - dropdownHeight - bottomSafeZone;
            // If still off screen top, clamp to top
            if (top < screenPadding) {
              top = screenPadding;
            }
          }
          
          setDropdownPosition({ top, left });
        }
      }
    } else {
      // Closing dropdown - clear highlight and position
      setMessageRect(null);
      setDropdownPosition(null);
    }
    
    setIsDropdownOpen(!isDropdownOpen);
    setIsEmojiPickerOpen(false);
  };

  const handleMenuItemClick = (action, e) => {
    e.stopPropagation();
    setIsDropdownOpen(false);
    setMessageRect(null);
    setDropdownPosition(null);
    action(message);
  };

  const handleEmojiButtonClick = (e) => {
    e.stopPropagation();
    
    if (!isEmojiPickerOpen) {
      // Opening emoji picker - capture message position and calculate dropdown position
      const messageElement = e.target.closest('[data-message-id]');
      if (messageElement) {
        const chatMessage = messageElement.querySelector('[data-chat-message]');
        if (chatMessage) {
          const rect = chatMessage.getBoundingClientRect();
          setMessageRect({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height
          });
          
          // Calculate emoji dropdown position
          const dropdownWidth = 200; // Approximate width (4 cols * 36px + padding)
          const dropdownHeight = 150; // Approximate height (3 rows * 36px + padding)
          const spacing = 16;
          const screenPadding = 10; // Minimum padding from screen edge
          
          let left = rect.right + spacing;
          let top = rect.top + (rect.height / 2) - (dropdownHeight / 2);
          
          // Check if dropdown would go off screen right
          const screenWidth = window.innerWidth;
          if (left + dropdownWidth > screenWidth - screenPadding) {
            // Try to position as far right as possible without going off screen
            left = screenWidth - dropdownWidth - screenPadding;
            
            // If that would overlap the message, try positioning to the left
            if (left < rect.right && rect.left - dropdownWidth - spacing > screenPadding) {
              left = rect.left - dropdownWidth - spacing;
            }
          }
          
          // Check if dropdown would go off screen top/bottom
          const screenHeight = window.innerHeight;
          if (top < screenPadding) {
            top = screenPadding;
          } else if (top + dropdownHeight > screenHeight - screenPadding) {
            top = screenHeight - dropdownHeight - screenPadding;
          }
          
          setEmojiDropdownPosition({ top, left });
        }
      }
    } else {
      // Closing emoji picker - clear highlight and position
      setMessageRect(null);
      setEmojiDropdownPosition(null);
    }
    
    setIsEmojiPickerOpen(!isEmojiPickerOpen);
    setIsDropdownOpen(false);
  };

  const handleEmojiSelect = (emoji, e) => {
    if (e) e.stopPropagation();
    
    console.log('handleEmojiSelect called with:', emoji);
    
    // Handle emoji from emoji-mart (it comes as an object) or string
    const emojiString = typeof emoji === 'string' ? emoji : emoji.native;
    
    console.log('Extracted emoji string:', emojiString);
    
    // Add unicode: prefix for new reactions
    const reactionType = `unicode:${emojiString}`;
    
    console.log('Reaction type:', reactionType);
    
    // Find any existing reaction from this user
    const existingReaction = Object.entries(message.userReactions || {}).find(
      ([reaction, userId]) => userId === currentUserId
    );
    
    console.log('Existing reaction:', existingReaction);
    
    // If user clicked the same reaction they already have, remove it
    if (existingReaction && (
      existingReaction[0] === reactionType || 
      (emojiString === '❤️' && existingReaction[0] === 'heart')
    )) {
      console.log('Removing existing reaction:', existingReaction[0]);
      onRemoveReaction?.(message.id, existingReaction[0]);
    } else {
      // If user has a different reaction, remove it first then add new one
      if (existingReaction) {
        console.log('Removing existing reaction and adding new one');
        onRemoveReaction?.(message.id, existingReaction[0]);
        // Add a small delay to ensure removal processes first
        setTimeout(() => {
          console.log('Adding new reaction after delay:', reactionType);
          onAddReaction?.(message.id, reactionType);
        }, 100);
      } else {
        // No existing reaction, just add the new one
        console.log('Adding new reaction:', reactionType);
        onAddReaction?.(message.id, reactionType);
      }
    }
    
    setIsEmojiPickerOpen(false);
    setShowFullEmojiPicker(false);
    setMessageRect(null);
    setEmojiDropdownPosition(null);
  };

  const handleSearchButtonClick = async (e) => {
    e.stopPropagation();
    
    if (!hasSubscription) {
      toast.error('Only subscribers can use other emojis');
      return;
    }
    
    // If picker is already loaded, just show it
    if (EmojiPickerComponent && emojiData) {
      setShowFullEmojiPicker(true);
      return;
    }
    
    setEmojiPickerLoading(true);
    
    try {
      // Dynamically import the React wrapper
      const { default: Picker } = await import('@emoji-mart/react');
      const { default: data } = await import('@emoji-mart/data');
      
      // Store the Picker component and data
      setEmojiPickerComponent(() => Picker);
      setEmojiData(data);
      setShowFullEmojiPicker(true);
    } catch (error) {
      console.error('Failed to load emoji picker:', error);
      toast.error('Failed to load emoji picker');
    } finally {
      setEmojiPickerLoading(false);
    }
  };

  // Check if this is a self message
  const isSelf = message.userId === 'self' || message.userId === currentUserId;

  return (
    <>
      <MenuContainer ref={menuRef}>
        <MenuButton
          onClick={handleMenuClick}
          $visible={isHovered || isDropdownOpen}
        >
          •••
        </MenuButton>

        {/* Only show emoji button for non-self messages and when reactions are enabled */}
        {!isSelf && onAddReaction && onRemoveReaction && (
          <div style={{ position: 'relative' }} ref={emojiRef}>
            <EmojiButton
              ref={emojiButtonRef}
              onClick={handleEmojiButtonClick}
              $visible={isHovered || isDropdownOpen || isEmojiPickerOpen}
            >
              😊
            </EmojiButton>
          </div>
        )}
      </MenuContainer>

      {/* Portal for fixed dropdown and message highlighting */}
      {isDropdownOpen && createPortal(
        <>
          {/* Message highlight overlay */}
          <MessageHighlightOverlay />
          
          {/* Highlighted message outline */}
          {messageRect && (
            <HighlightedMessageOutline
              style={{
                top: messageRect.top,
                left: messageRect.left,
                width: messageRect.width,
                height: messageRect.height,
              }}
            />
          )}
          
          {/* Fixed dropdown menu */}
          <DropdownMenu 
            data-dropdown-menu
            style={dropdownPosition ? {
              top: dropdownPosition.top,
              left: dropdownPosition.left
            } : {}}>
            {onReply && (
              <MenuItem onClick={(e) => handleMenuItemClick(onReply, e)}>
                <span>↩️</span>
                Reply
              </MenuItem>
            )}

            <MenuItem onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(message.messageText || '');
              toast.success('Message copied to clipboard');
              setIsDropdownOpen(false);
              setMessageRect(null);
              setDropdownPosition(null);
            }}>
              <span>📋</span>
              Copy text
            </MenuItem>

            {hasReactions && (
              <MenuItem onClick={(e) => {
                e.stopPropagation();
                handleViewReactions();
              }}>
                <span>😊</span>
                View reactions
              </MenuItem>
            )}

            {/* Only show other options for non-self messages */}
            {!isSelf && (
              <>
                {onViewProfile && (
                  <MenuItem onClick={(e) => handleMenuItemClick(onViewProfile, e)}>
                    <span>👤</span>
                    View Profile
                  </MenuItem>
                )}

                {onFriendRequest && (
                  <MenuItem onClick={(e) => handleMenuItemClick(onFriendRequest, e)}>
                    <span>👋</span>
                    Friend request
                  </MenuItem>
                )}

                {onReport && (
                  <MenuItem onClick={(e) => handleMenuItemClick(onReport, e)}>
                    <span>⚠️</span>
                    Report message
                  </MenuItem>
                )}

                {onBlock && !isUserBlocked(message.userId) && (
                  <MenuItem onClick={(e) => handleMenuItemClick(onBlock, e)}>
                    <span>🚫</span>
                    Block this user
                  </MenuItem>
                )}

                {isMod && onWarn && (
                  <WarnMenuItem onClick={(e) => handleMenuItemClick(onWarn, e)}>
                    <span>⚠️</span>
                    Warn user
                  </WarnMenuItem>
                )}

                {isMod && onBan && (
                  <BanMenuItem onClick={(e) => handleMenuItemClick(onBan, e)}>
                    <span>🔨</span>
                    Ban user
                  </BanMenuItem>
                )}
              </>
            )}
          </DropdownMenu>
        </>,
        document.body
      )}

      {/* Portal for emoji dropdown and message highlighting */}
      {isEmojiPickerOpen && createPortal(
        <>
          {/* Message highlight overlay */}
          <MessageHighlightOverlay />
          
          {/* Highlighted message outline */}
          {messageRect && (
            <HighlightedMessageOutline
              style={{
                top: messageRect.top,
                left: messageRect.left,
                width: messageRect.width,
                height: messageRect.height,
              }}
            />
          )}
          
          {/* Fixed emoji dropdown */}
          <EmojiDropdown 
            data-emoji-dropdown
            style={emojiDropdownPosition ? {
              top: emojiDropdownPosition.top,
              left: emojiDropdownPosition.left
            } : {}}
          >
            {emojiOptions.map((emoji) => {
              // Find any existing reaction from this user
              const existingReaction = Object.entries(message.userReactions || {}).find(
                ([reaction, userId]) => userId === currentUserId
              );
              
              // Check if this emoji is the user's current reaction
              const isCurrentReaction = existingReaction && (
                existingReaction[0] === `unicode:${emoji}` ||
                (emoji === '❤️' && existingReaction[0] === 'heart')
              );
              
              return (
                <EmojiOption
                  key={emoji}
                  onClick={(e) => handleEmojiSelect(emoji, e)}
                  style={{
                    opacity: isCurrentReaction ? 1 : 0.7,
                    transform: isCurrentReaction ? 'scale(1.1)' : 'scale(1)',
                  }}
                >
                  {emoji}
                </EmojiOption>
              );
            })}
            
            {/* Search button underneath emoji options */}
            <SearchButton 
              onClick={handleSearchButtonClick}
              disabled={showFullEmojiPicker || emojiPickerLoading}
            >
              {emojiPickerLoading ? '⏳ Loading...' : '🔍 Search more emojis'}
            </SearchButton>
          </EmojiDropdown>
        </>,
        document.body
      )}

      {/* Portal for full emoji picker */}
      {showFullEmojiPicker && createPortal(
        <>
          {/* Message highlight overlay */}
          <MessageHighlightOverlay />
          
          {/* Highlighted message outline */}
          {messageRect && (
            <HighlightedMessageOutline
              style={{
                top: messageRect.top,
                left: messageRect.left,
                width: messageRect.width,
                height: messageRect.height,
              }}
            />
          )}
          
          {/* Full emoji picker */}
          <FullEmojiPickerContainer 
            data-full-emoji-picker
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: (() => {
                if (!emojiDropdownPosition) return '50%';
                
                const pickerHeight = 400; // Approximate emoji picker height (increased)
                const screenHeight = window.innerHeight;
                const screenPadding = 60; // More padding for safety
                
                let top = emojiDropdownPosition.top;
                
                // If picker would go off bottom of screen, position it higher
                if (top + pickerHeight > screenHeight - screenPadding) {
                  top = screenHeight - pickerHeight - screenPadding;
                }
                
                // If picker would go off top of screen, position it lower
                if (top < screenPadding) {
                  top = screenPadding;
                }
                
                return top;
              })(),
              left: (() => {
                if (!emojiDropdownPosition) return '50%';
                
                const pickerWidth = 350; // Approximate emoji picker width (increased)
                const screenWidth = window.innerWidth;
                const screenPadding = 40; // More padding for safety
                
                let left = emojiDropdownPosition.left;
                
                // If picker would go off right side of screen, position it further left
                if (left + pickerWidth > screenWidth - screenPadding) {
                  left = screenWidth - pickerWidth - screenPadding;
                }
                
                // If picker would go off left side of screen, position it further right
                if (left < screenPadding) {
                  left = screenPadding;
                }
                
                return left;
              })(),
              transform: emojiDropdownPosition ? 'none' : 'translate(-50%, -50%)',
              zIndex: 10000,
            }}
          >
            {EmojiPickerComponent && emojiData ? (
              <EmojiPickerComponent 
                data={emojiData}
                theme="dark"
                previewPosition="none"
                skinTonePosition="none"
                onEmojiSelect={(emoji) => {
                  handleEmojiSelect(emoji);
                }}
                onClickOutside={() => {
                  // Close full picker and return to small emoji dropdown
                  setShowFullEmojiPicker(false);
                }}
              />
            ) : (
              <div style={{ 
                padding: '20px', 
                color: 'var(--clr-sec)', 
                textAlign: 'center' 
              }}>
                Loading emoji picker...
              </div>
            )}
          </FullEmojiPickerContainer>
        </>,
        document.body
      )}

      {/* Reactions modal */}
      {showReactionsModal && createPortal(
        <ReactionsModalOverlay onClick={() => {
          setShowReactionsModal(false);
          setReactionsData({});
          setActiveReactionTab(null);
        }}>
          <ReactionsModal onClick={(e) => e.stopPropagation()}>
            <ReactionsModalHeader>
              <ReactionsModalTitle>Reactions</ReactionsModalTitle>
              <CloseButton onClick={() => {
                setShowReactionsModal(false);
                setReactionsData({});
                setActiveReactionTab(null);
              }}>
                ×
              </CloseButton>
            </ReactionsModalHeader>

            <ReactionTabs>
              {activeReactions.map(([reactionType, count]) => (
                <ReactionTab
                  key={reactionType}
                  $active={activeReactionTab === reactionType}
                  onClick={() => handleTabChange(reactionType)}
                >
                  {getEmojiFromType(reactionType)}
                  <ReactionCount>{count}</ReactionCount>
                </ReactionTab>
              ))}
            </ReactionTabs>

            <UsersList>
              {loadingReactions && !reactionsData[activeReactionTab] ? (
                <LoadingText>Loading...</LoadingText>
              ) : reactionsData[activeReactionTab]?.length > 0 ? (
                reactionsData[activeReactionTab].map((user, index) => (
                  <UserItem key={user.userId || index}>
                    {user.nickname || 'Anonymous'}
                  </UserItem>
                ))
              ) : (
                <LoadingText>No reactions yet</LoadingText>
              )}
            </UsersList>
          </ReactionsModal>
        </ReactionsModalOverlay>,
        document.body
      )}
    </>
  );
};

export default MessageActionsMenu;
